if (!customElements.get('wlsn-dome-refill-buybox')) {
  class WLSNDomeRefillBuybox extends HTMLElement {
    constructor() {
      super();

      this.onOptionChange = this.onOptionChange.bind(this);
      this.onPurchaseChange = this.onPurchaseChange.bind(this);
      this.onDecrease = this.onDecrease.bind(this);
      this.onIncrease = this.onIncrease.bind(this);
      this.onAddToCart = this.onAddToCart.bind(this);
      this.onGalleryPrevious = this.onGalleryPrevious.bind(this);
      this.onGalleryNext = this.onGalleryNext.bind(this);
      this.requestCounter = 0;
      this.abortController = null;
      this.isUpdating = false;
    }

    connectedCallback() {
      this.variants = this.readVariants();
      this.optionInputs = Array.from(this.querySelectorAll('.js-wlsn-dome-refill-option'));
      this.purchaseInputs = Array.from(this.querySelectorAll('.js-wlsn-dome-refill-purchase'));
      this.quantityInput = this.querySelector('.js-wlsn-dome-refill-quantity');
      this.decreaseButton = this.querySelector('.js-wlsn-dome-refill-decrease');
      this.increaseButton = this.querySelector('.js-wlsn-dome-refill-increase');
      this.addToCartButton = this.querySelector('.js-wlsn-dome-refill-atc');
      this.addToCartLabel = this.querySelector('.js-wlsn-dome-refill-atc-label');
      this.gallerySlides = Array.from(this.querySelectorAll('.js-wlsn-dome-refill-gallery-slide'));
      this.galleryTrack = this.querySelector('.js-wlsn-dome-refill-gallery');
      this.galleryPrevious = this.querySelector('.js-wlsn-slider-prev');
      this.galleryNext = this.querySelector('.js-wlsn-slider-next');
      this.drawer = document.querySelector('wlsn-cart-drawer');

      this.optionInputs.forEach((input) => input.addEventListener('change', this.onOptionChange));
      this.purchaseInputs.forEach((input) => input.addEventListener('change', this.onPurchaseChange));
      this.decreaseButton?.addEventListener('click', this.onDecrease);
      this.increaseButton?.addEventListener('click', this.onIncrease);
      this.addToCartButton?.addEventListener('click', this.onAddToCart);
      this.galleryPrevious?.addEventListener('click', this.onGalleryPrevious);
      this.galleryNext?.addEventListener('click', this.onGalleryNext);

      const initialVariantId = Number(this.addToCartButton?.dataset.variantId);
      this.currentVariant = this.variants.find((variant) => variant.id === initialVariantId) || null;

      this.refreshVisibleGallerySlides();
      this.ensureVisibleGallerySlide(false);
    }

    disconnectedCallback() {
      this.optionInputs?.forEach((input) => input.removeEventListener('change', this.onOptionChange));
      this.purchaseInputs?.forEach((input) => input.removeEventListener('change', this.onPurchaseChange));
      this.decreaseButton?.removeEventListener('click', this.onDecrease);
      this.increaseButton?.removeEventListener('click', this.onIncrease);
      this.addToCartButton?.removeEventListener('click', this.onAddToCart);
      this.galleryPrevious?.removeEventListener('click', this.onGalleryPrevious);
      this.galleryNext?.removeEventListener('click', this.onGalleryNext);
    }

    readVariants() {
      const script = this.querySelector('.js-wlsn-dome-refill-product-json');

      try {
        return JSON.parse(script?.textContent || '[]');
      } catch (error) {
        console.error('Dome refill buybox product data could not be parsed.', error);
        return [];
      }
    }

    onOptionChange(event) {
      if (this.isUpdating) return;

      const group = event.target.closest('.wlsn-dome-refill-buybox__option-group');
      const selectedLabel = group?.querySelector('.js-wlsn-dome-refill-selected-value');

      if (selectedLabel) selectedLabel.textContent = event.target.dataset.displayValue || event.target.value;
      if (event.target.dataset.optionRole === 'style') this.applyGalleryStyleFilter();
      this.showGalleryImageForOption(event.target);
      this.ensureVisibleGallerySlide();
      this.updateUrl();
      this.fetchVariantMarkup(event.target.dataset.optionPosition, event.target.dataset.optionValueId);
    }

    onPurchaseChange() {
      this.updateUrl();
    }

    onDecrease() {
      this.setQuantity((parseInt(this.quantityInput?.value, 10) || 1) - 1);
    }

    onIncrease() {
      this.setQuantity((parseInt(this.quantityInput?.value, 10) || 1) + 1);
    }

    setQuantity(quantity) {
      if (this.quantityInput) this.quantityInput.value = Math.max(1, quantity);
    }

    onGalleryPrevious() {
      this.changeGallerySlide(-1);
    }

    onGalleryNext() {
      this.changeGallerySlide(1);
    }

    changeGallerySlide(direction) {
      if (!this.visibleGallerySlides.length) return;

      const currentIndex = Math.max(
        0,
        this.visibleGallerySlides.findIndex((slide) => slide.classList.contains('is-active'))
      );
      const nextIndex =
        (currentIndex + direction + this.visibleGallerySlides.length) % this.visibleGallerySlides.length;
      this.setGallerySlide(this.visibleGallerySlides[nextIndex]);
    }

    refreshVisibleGallerySlides() {
      this.visibleGallerySlides = this.gallerySlides.filter((slide) => !slide.classList.contains('is-hidden'));
    }

    applyGalleryStyleFilter() {
      const selectedStyle =
        this.querySelector('.js-wlsn-dome-refill-option[data-option-role="style"]:checked')?.value || '';

      this.gallerySlides.forEach((slide) => {
        // No styles listed means the media is shared by every style.
        const slideStyles = (slide.dataset.mediaStyles || '').split('||').filter(Boolean);
        slide.classList.toggle('is-hidden', slideStyles.length > 0 && !slideStyles.includes(selectedStyle));
      });

      this.refreshVisibleGallerySlides();
    }

    getNextVisibleGallerySlide(fromSlide) {
      const fromIndex = fromSlide ? this.gallerySlides.indexOf(fromSlide) : -1;

      // Nearest visible slide to the right, wrapping only when there is none.
      for (let offset = 1; offset <= this.gallerySlides.length; offset += 1) {
        const slide = this.gallerySlides[(fromIndex + offset) % this.gallerySlides.length];
        if (!slide.classList.contains('is-hidden')) return slide;
      }

      return this.visibleGallerySlides[0];
    }

    ensureVisibleGallerySlide(animate = true) {
      if (!this.visibleGallerySlides.length) return;

      const activeSlide = this.gallerySlides.find((slide) => slide.classList.contains('is-active'));
      const targetSlide =
        activeSlide && !activeSlide.classList.contains('is-hidden')
          ? activeSlide
          : this.getNextVisibleGallerySlide(activeSlide);

      // Hiding slides shifts the active slide's visible index; a no-op when already positioned.
      this.setGallerySlide(targetSlide, animate);
    }

    setGallerySlide(activeSlide, animate = true) {
      const activeIndex = this.visibleGallerySlides.indexOf(activeSlide);
      if (activeIndex < 0 || !this.galleryTrack) return;

      if (!animate) this.galleryTrack.style.transition = 'none';
      this.galleryTrack.style.transform = `translate3d(-${activeIndex * 100}%, 0, 0)`;
      this.gallerySlides.forEach((slide) => slide.classList.toggle('is-active', slide === activeSlide));

      if (!animate) {
        requestAnimationFrame(() => {
          this.galleryTrack.style.removeProperty('transition');
        });
      }
    }

    showGalleryImageForOption(input, animate = true) {
      const optionImage = input
        .closest('.wlsn-dome-refill-buybox__option-item')
        ?.querySelector('.wlsn-dome-refill-buybox__style-image');

      if (!optionImage) return;

      const optionImageName = this.getImageFileName(optionImage);
      if (!optionImageName) return;

      let matchedSlide = this.visibleGallerySlides.find((slide) => {
        const galleryImage = slide.querySelector('.wlsn-dome-refill-buybox__gallery-image');
        return galleryImage && this.getImageFileName(galleryImage) === optionImageName;
      });

      if (!matchedSlide && this.galleryTrack) {
        matchedSlide = document.createElement('div');
        matchedSlide.className = 'wlsn-dome-refill-buybox__gallery-slide js-wlsn-dome-refill-gallery-slide';
        matchedSlide.dataset.optionImage = optionImageName;
        // Owned by the style it was built from, so later filters hide it.
        if (input.dataset.optionRole === 'style') matchedSlide.dataset.mediaStyles = input.value;

        const image = document.createElement('img');
        image.className = 'wlsn-dome-refill-buybox__gallery-image';
        image.src = optionImage.currentSrc || optionImage.src;
        image.alt = optionImage.alt || '';
        matchedSlide.appendChild(image);
        // First, not last, or the next click wraps straight back to the start.
        // gallerySlides must stay in DOM order for the translate index to be right.
        this.galleryTrack.prepend(matchedSlide);
        this.gallerySlides.unshift(matchedSlide);
        this.refreshVisibleGallerySlides();
      }

      if (matchedSlide) this.setGallerySlide(matchedSlide, animate);
    }

    getImageFileName(image) {
      const imageUrl = image?.currentSrc || image?.src;
      if (!imageUrl) return '';

      try {
        return decodeURIComponent(new URL(imageUrl, window.location.origin).pathname)
          .split('/')
          .pop()
          .replace(/\.(jpg|jpeg|png|webp|gif|avif)$/i, '')
          .replace(/_(\d+)x(\d+|progressive)?(@2x)?$/i, '')
          .replace(/_\d+x$/i, '')
          .toLowerCase();
      } catch (error) {
        return '';
      }
    }

    updateUrl() {
      const url = new URL(window.location.href);
      const optionValueIds = Array.from(this.querySelectorAll('.js-wlsn-dome-refill-option:checked'))
        .sort((a, b) => Number(a.dataset.optionPosition) - Number(b.dataset.optionPosition))
        .map((input) => input.dataset.optionValueId)
        .filter(Boolean);

      url.searchParams.delete('variant');
      url.searchParams.delete('selling_plan');

      if (optionValueIds.length) url.searchParams.set('option_values', optionValueIds.join(','));
      else url.searchParams.delete('option_values');

      window.history.replaceState({}, '', url);
    }

    async fetchVariantMarkup(changedOptionPosition = '', changedOptionValueId = '') {
      const optionValueIds = Array.from(this.querySelectorAll('.js-wlsn-dome-refill-option:checked'))
        .sort((a, b) => Number(a.dataset.optionPosition) - Number(b.dataset.optionPosition))
        .map((input) => input.dataset.optionValueId)
        .filter(Boolean);

      if (!optionValueIds.length) return;

      const currentRequest = ++this.requestCounter;
      this.abortController?.abort();
      this.abortController = new AbortController();
      this.isUpdating = true;
      this.classList.add('is-updating');
      const optionsRegion = this.querySelector('.js-wlsn-dome-refill-region-options');
      optionsRegion?.setAttribute('inert', '');
      optionsRegion?.setAttribute('aria-busy', 'true');
      const selectedPurchaseValue = this.querySelector('.js-wlsn-dome-refill-purchase:checked')?.value ?? '';

      try {
        const url = new URL(window.location.href);
        url.searchParams.set('option_values', optionValueIds.join(','));
        url.searchParams.delete('variant');
        url.searchParams.delete('selling_plan');

        const sectionId = this.dataset.sectionId;
        let incomingBuybox = null;
        let requestedOptionValueIds = optionValueIds;

        for (let attempt = 0; attempt < 3; attempt += 1) {
          url.searchParams.set('option_values', requestedOptionValueIds.join(','));

          const response = await fetch(url, { signal: this.abortController.signal });
          if (!response.ok) throw new Error(`Variant request failed with ${response.status}`);

          const html = await response.text();
          if (currentRequest !== this.requestCounter) return;

          const doc = new DOMParser().parseFromString(html, 'text/html');
          incomingBuybox = doc.querySelector(`wlsn-dome-refill-buybox[data-section-id="${sectionId}"]`);
          if (!incomingBuybox) throw new Error('Updated dome refill buybox markup was not found.');

          const correctedOptionValueIds = this.getCorrectedOptionValueIds(
            incomingBuybox,
            changedOptionPosition,
            changedOptionValueId
          );
          if (!correctedOptionValueIds.length || correctedOptionValueIds.join(',') === requestedOptionValueIds.join(',')) break;

          requestedOptionValueIds = correctedOptionValueIds;
        }

        if (!incomingBuybox) return;

        url.searchParams.set('option_values', requestedOptionValueIds.join(','));
        window.history.replaceState({}, '', url);

        this.syncVariantMarkup(incomingBuybox, selectedPurchaseValue);
      } catch (error) {
        if (error.name !== 'AbortError') console.error('Dome refill variant update failed.', error);
      } finally {
        if (currentRequest === this.requestCounter) {
          this.isUpdating = false;
          this.classList.remove('is-updating');
          const currentOptionsRegion = this.querySelector('.js-wlsn-dome-refill-region-options');
          currentOptionsRegion?.removeAttribute('inert');
          currentOptionsRegion?.removeAttribute('aria-busy');
        }
      }
    }

    getCorrectedOptionValueIds(incomingBuybox, changedOptionPosition = '', changedOptionValueId = '') {
      return Array.from(incomingBuybox.querySelectorAll('.wlsn-dome-refill-buybox__option-group'))
        .sort((a, b) => Number(a.dataset.optionPosition) - Number(b.dataset.optionPosition))
        .map((group) => {
          const inputs = Array.from(group.querySelectorAll('.js-wlsn-dome-refill-option'));
          const selectedIndex = inputs.findIndex((input) => input.checked);
          const selectedInput = inputs[selectedIndex];
          const isChangedGroup = group.dataset.optionPosition === changedOptionPosition;

          if (isChangedGroup && changedOptionValueId) return changedOptionValueId;
          if (selectedInput && !selectedInput.disabled) return selectedInput.dataset.optionValueId;

          const startingIndex = selectedIndex >= 0 ? selectedIndex : -1;
          for (let offset = 1; offset <= inputs.length; offset += 1) {
            const index = ((startingIndex + offset) % inputs.length);
            if (!inputs[index].disabled) return inputs[index].dataset.optionValueId;
          }

          return selectedInput?.dataset.optionValueId || '';
        })
        .filter(Boolean);
    }

    syncVariantMarkup(incomingBuybox, selectedPurchaseValue = '') {
      const incomingOptionGroups = Array.from(incomingBuybox.querySelectorAll('.wlsn-dome-refill-buybox__option-group'));

      incomingOptionGroups.forEach((incomingGroup) => {
        const position = incomingGroup.dataset.optionPosition;
        const currentGroup = this.querySelector(`.wlsn-dome-refill-buybox__option-group[data-option-position="${position}"]`);
        if (!currentGroup) return;

        const incomingSelectedValue = incomingGroup.querySelector('.js-wlsn-dome-refill-selected-value');
        const currentSelectedValue = currentGroup.querySelector('.js-wlsn-dome-refill-selected-value');
        if (incomingSelectedValue && currentSelectedValue) currentSelectedValue.textContent = incomingSelectedValue.textContent;

        incomingGroup.querySelectorAll('.js-wlsn-dome-refill-option').forEach((incomingInput) => {
          const valueId = incomingInput.dataset.optionValueId;
          const currentInput = currentGroup.querySelector(`.js-wlsn-dome-refill-option[data-option-value-id="${valueId}"]`);
          if (!currentInput) return;

          currentInput.checked = incomingInput.checked;
          currentInput.disabled = incomingInput.disabled;
          if (incomingInput.disabled) currentInput.setAttribute('aria-disabled', 'true');
          else currentInput.removeAttribute('aria-disabled');

          const currentItem = currentInput.closest('.wlsn-dome-refill-buybox__option-item');
          const incomingItem = incomingInput.closest('.wlsn-dome-refill-buybox__option-item');
          currentItem?.classList.toggle('is-unavailable', incomingItem?.classList.contains('is-unavailable'));
        });
      });

      const incomingPurchaseInputs = Array.from(incomingBuybox.querySelectorAll('.js-wlsn-dome-refill-purchase'));
      this.purchaseInputs.forEach((currentInput) => {
        const incomingInput = incomingPurchaseInputs.find((input) => input.value === currentInput.value);
        if (!incomingInput) return;

        const currentPrice = currentInput.nextElementSibling?.querySelector('.wlsn-dome-refill-buybox__purchase-price');
        const incomingPrice = incomingInput.nextElementSibling?.querySelector('.wlsn-dome-refill-buybox__purchase-price');
        if (currentPrice && incomingPrice) currentPrice.textContent = incomingPrice.textContent;
        currentInput.disabled = incomingInput.disabled;
      });

      const selectedPurchaseInput = this.purchaseInputs.find((input) => input.value === selectedPurchaseValue && !input.disabled);
      const fallbackPurchaseInput = this.purchaseInputs.find((input) => !input.disabled);
      if (selectedPurchaseInput || fallbackPurchaseInput) (selectedPurchaseInput || fallbackPurchaseInput).checked = true;

      const incomingAddToCartButton = incomingBuybox.querySelector('.js-wlsn-dome-refill-atc');
      if (this.addToCartButton && incomingAddToCartButton) {
        this.addToCartButton.dataset.variantId = incomingAddToCartButton.dataset.variantId || '';
        this.addToCartButton.disabled = incomingAddToCartButton.disabled;
        if (this.addToCartLabel) this.addToCartLabel.textContent = incomingAddToCartButton.textContent.trim();
      }

      const updatedVariantId = Number(this.addToCartButton?.dataset.variantId);
      this.currentVariant = this.variants.find((variant) => variant.id === updatedVariantId) || null;

      // The response can move the selection to an available style.
      this.applyGalleryStyleFilter();
      this.ensureVisibleGallerySlide();
    }

    async onAddToCart(event) {
      event.preventDefault();
      if (!this.currentVariant?.available || this.addToCartButton.disabled) return;

      const payload = {
        id: this.currentVariant.id,
        quantity: parseInt(this.quantityInput?.value, 10) || 1
      };
      const sellingPlan = this.querySelector('.js-wlsn-dome-refill-purchase:checked')?.value;
      if (sellingPlan) payload.selling_plan = Number(sellingPlan);

      this.addToCartButton.disabled = true;
      this.classList.add('is-loading');

      try {
        const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });

        if (!response.ok) throw new Error(await response.text());

        if (window.cartType === 'cart_drawer' && this.drawer) {
          this.drawer.openDrawer();
          this.drawer.setLoading(true);
          await this.drawer.ensureHydrated();
          await this.drawer.refreshDrawer();
        } else {
          window.location.href = `${window.Shopify?.routes?.root || '/'}cart`;
        }
      } catch (error) {
        console.error('Dome refill add to cart failed.', error);
      } finally {
        this.classList.remove('is-loading');
        this.addToCartButton.disabled = !this.currentVariant?.available;
      }
    }
  }

  customElements.define('wlsn-dome-refill-buybox', WLSNDomeRefillBuybox);
}
